import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";

type Slot = { date: string; day_of_week: number; start_time: string; end_time: string; price: number | null };
type Resource = { type: "power" | "venue"; id: string; label: string; category?: string };

const DAY_LABELS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const MONTH_LABELS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const fmtDate = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return `${DAY_LABELS[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
};
const fmtDateFull = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  const day = DAY_LABELS[d.getDay()];
  return `${day.charAt(0).toUpperCase() + day.slice(1)} ${d.getDate()} ${MONTH_LABELS[d.getMonth()]}`;
};
const ChevronLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
);
const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18"/></svg>
);
const ClockRowIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg>
);
const PersonRowIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>
);
const NoteRowIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v12H8l-4 4z"/></svg>
);

// Une rangée d'info éditable, même registre visuel que l'écran "Paiement" d'Uber Eats (icône,
// libellé, valeur, chevron pour revenir en arrière et changer). Reprend le style déjà en place
// dans le Centre des règles (icône dans un badge, rangées à séparateurs fins).
const ReviewRow = ({ icon, label, value, onEdit }: { icon: React.ReactNode; label: string; value: string; onEdit?: () => void }) => (
  <div onClick={onEdit} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: "1px solid #F0F0F0", cursor: onEdit ? "pointer" : "default" }}>
    <div style={{ width: 38, height: 38, borderRadius: 12, backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, color: "#999", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "#0a0a0a" }}>{value}</div>
    </div>
    {onEdit && <ChevronRightIcon />}
  </div>
);

// Réservation d'un créneau concret proposé par le prestataire ou le lieu — la disponibilité
// vient entièrement de son agenda, jamais d'une heure inventée par le demandeur. Deux étapes
// désormais ("slots" puis "review") plutôt qu'un seul écran qui filait droit sur Stripe : un vrai
// écran de récapitulatif façon "Paiement" (Uber Eats) entre le choix du créneau et le paiement,
// pour qu'on relise ce qu'on est en train de payer avant de partir sur Stripe, jamais une
// confirmation à l'aveugle.
export default function BookingModal({ resource, providerName, onClose, onBooked }: {
  resource: Resource; providerName: string; onClose: () => void; onBooked: () => void;
}) {
  const { lang } = useApp();
  const [step, setStep] = useState<"slots" | "review">("slots");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Slot | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isVenue = resource.type === "venue";
  const slotsPath = isVenue ? `/venues/${resource.id}/slots?category=${encodeURIComponent(resource.category || "")}` : `/bookings/slots/${resource.id}`;
  const submitPath = isVenue ? `/venues/${resource.id}/bookings` : `/bookings`;
  const isPaid = selected?.price != null && Number(selected.price) > 0;

  useEffect(() => {
    api.get(slotsPath).then(data => setSlots(data?.slots || [])).catch(() => {}).finally(() => setLoading(false));
  }, [resource.id, resource.category]);

  const submit = () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    const body: any = { booking_date: selected.date, start_time: selected.start_time, end_time: selected.end_time, message: message.trim() || undefined, lang };
    if (!isVenue) body.power_id = resource.id;
    else body.category = resource.category || null;
    if (isPaid) {
      body.success_url = `${window.location.origin}${window.location.pathname}?payment=success`;
      body.cancel_url = `${window.location.origin}${window.location.pathname}?payment=cancelled`;
    }
    api.post(submitPath, body)
      .then(data => {
        if (data?.checkoutUrl) window.location.href = data.checkoutUrl;
        else if (data?.booking) { setDone(true); onBooked(); setTimeout(onClose, 1500); }
        else alert(data?.error || "Erreur lors de la réservation");
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSubmitting(false));
  };

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 24, padding: 24, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>

        {step === "slots" || done ? (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.3 }}>Réserver un créneau</span>
              <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{resource.label} · {providerName}</div>

            {done ? (
              <div style={{ padding: "30px 0", textAlign: "center" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Demande de réservation envoyée</div>
                <div style={{ fontSize: 12.5, color: "#999", marginTop: 4 }}>{providerName} doit encore la confirmer.</div>
              </div>
            ) : loading ? (
              <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>Chargement des disponibilités...</div>
            ) : slots.length === 0 ? (
              <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>Aucun créneau disponible pour l'instant.</div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
                  {slots.map((s, i) => {
                    const isSel = selected?.date === s.date && selected?.start_time === s.start_time;
                    return (
                      <button key={i} onClick={() => setSelected(s)} style={{ padding: "12px 8px", borderRadius: 14, border: isSel ? "none" : "1.5px solid #E5E5E5", backgroundColor: isSel ? "#0a0a0a" : "#FAFAFA", color: isSel ? "white" : "#333", cursor: "pointer", textAlign: "center" }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{fmtDate(s.date)}</div>
                        <div style={{ fontSize: 11.5, marginTop: 2, opacity: 0.85 }}>{s.start_time.slice(0, 5)} – {s.end_time.slice(0, 5)}</div>
                        {s.price != null && <div style={{ fontSize: 11.5, marginTop: 2, fontWeight: 700 }}>{Number(s.price).toFixed(2)} €</div>}
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => setStep("review")} disabled={!selected} style={{ width: "100%", padding: "15px", borderRadius: 16, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: !selected ? 0.4 : 1 }}>
                  Continuer
                </button>
              </>
            )}
          </>
        ) : selected && (
          <>
            {/* Étape 2 — récapitulatif façon "Paiement", entre le choix du créneau et Stripe :
                relire ce qu'on est en train de payer avant de partir, jamais une confirmation
                à l'aveugle. */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
              <button onClick={() => setStep("slots")} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex" }}><ChevronLeftIcon /></button>
              <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.3, flex: 1, textAlign: "center", marginRight: 18 }}>Paiement</span>
            </div>

            <div style={{ marginBottom: 8 }}>
              <ReviewRow icon={<PersonRowIcon />} label={resource.type === "venue" ? "Lieu" : "Prestataire"} value={`${providerName} · ${resource.label}`} />
              <ReviewRow icon={<ClockRowIcon />} label="Créneau" value={`${fmtDateFull(selected.date)}, ${selected.start_time.slice(0, 5)}–${selected.end_time.slice(0, 5)}`} onEdit={() => setStep("slots")} />
              <ReviewRow icon={<NoteRowIcon />} label="Message pour le prestataire (optionnel)" value={message.trim() || "Ajouter un mot"} />
            </div>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Un mot pour préciser ta demande..." maxLength={300} style={{ width: "100%", minHeight: 56, padding: "10px 12px", borderRadius: 12, border: "1px solid #E5E5E5", fontSize: 13.5, outline: "none", margin: "4px 0 20px", boxSizing: "border-box", resize: "none" as const }} />

            <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#999", marginBottom: 12 }}>Récapitulatif</p>
            <div style={{ backgroundColor: "#F5F5F5", borderRadius: 16, padding: "16px 18px", marginBottom: 22 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: isPaid ? 8 : 0 }}>
                <span style={{ color: "#666" }}>{resource.label}</span>
                <span style={{ fontWeight: 700 }}>{isPaid ? `${Number(selected.price).toFixed(2)} €` : "Gratuit"}</span>
              </div>
              {isPaid && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15.5, fontWeight: 800, paddingTop: 8, borderTop: "1px solid #E5E5E5" }}>
                  <span>Total</span>
                  <span>{Number(selected.price).toFixed(2)} €</span>
                </div>
              )}
            </div>

            <button onClick={submit} disabled={submitting} style={{ width: "100%", padding: "16px", borderRadius: 16, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
              {submitting ? "..." : isPaid ? "Confirmer et payer" : "Confirmer la réservation"}
            </button>
          </>
        )}
      </div>
    </div>
  ,
    document.body
  );
}
